import React from "react";
import ReactECharts from "echarts-for-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as echarts from "echarts";
import clsx from "clsx";

import { listSites } from "../../api/sites";
import { getTrackerSiteReport, regenerateTrackerReport } from "../../api/reports";
import { useAuth } from "../../auth/AuthContext";

type SiteOption = { id: number; name: string };
type ToggleCardProps = { title: string; children: React.ReactNode; defaultOpen?: boolean; action?: React.ReactNode };

const PDF_BLUE: [number, number, number] = [0, 82, 155];
const PAGE_MARGIN_PT = 36;
const GREEN: [number, number, number] = [16, 185, 129];
const ORANGE: [number, number, number] = [245, 158, 11];
const RED: [number, number, number] = [239, 68, 68];
const SLATE: [number, number, number] = [51, 65, 85];

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

const chartToDataUrl = async (option: any, width = 1000, height = 420) => {
  if (!option) return null;
  const safeOption = { ...option, animation: false };
  const el = document.createElement("div");
  el.style.position = "fixed";
  el.style.left = "-99999px";
  el.style.width = `${width}px`;
  el.style.height = `${height}px`;
  document.body.appendChild(el);
  const instance = echarts.init(el, undefined, { renderer: "canvas", width, height });
  instance.setOption(safeOption, true);
  await sleep(80);
  instance.resize({ width, height });
  const dataUrl = instance.getDataURL({ pixelRatio: 1.5, backgroundColor: "#ffffff" });
  instance.dispose();
  el.remove();
  return dataUrl;
};

const ToggleCard: React.FC<ToggleCardProps> = ({ title, children, defaultOpen = false, action }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</div>
          <button className="text-xs font-medium text-brand-700 underline" onClick={() => setOpen((value) => !value)}>
            {open ? "Collapse" : "Show"}
          </button>
        </div>
        {action ? <div className="flex items-center gap-2">{action}</div> : null}
      </div>
      {open ? <div className="border-t border-slate-100 px-5 py-4">{children}</div> : null}
    </div>
  );
};

const formatNumber = (value: number | null | undefined, precision = 1, suffix = "") => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${Number(value).toFixed(precision)}${suffix}`;
};

const formatPercent = (value: number | null | undefined, precision = 1) => formatNumber(value, precision, "%");

const formatCount = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString();
};

const formatDateLabel = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return date.toLocaleDateString();
};

const ratioPercent = (part: number | null | undefined, total: number | null | undefined, precision = 1) => {
  if (
    part === null ||
    typeof part === "undefined" ||
    total === null ||
    typeof total === "undefined" ||
    Number.isNaN(Number(part)) ||
    Number.isNaN(Number(total)) ||
    Number(total) <= 0
  ) {
    return "—";
  }
  return `${((Number(part) / Number(total)) * 100).toFixed(precision)}%`;
};

const statusTone = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "slate";
  if (value >= 90) return "emerald";
  if (value >= 70) return "amber";
  return "rose";
};

const toneClasses: Record<string, string> = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  rose: "border-rose-200 bg-rose-50 text-rose-800",
  blue: "border-blue-200 bg-blue-50 text-blue-800",
  slate: "border-slate-200 bg-slate-50 text-slate-700",
};

const SummaryKpi: React.FC<{ label: string; value: string; hint?: string; tone?: keyof typeof toneClasses }> = ({ label, value, hint, tone = "slate" }) => (
  <div className={clsx("rounded-2xl border px-4 py-4 shadow-sm", toneClasses[tone])}>
    <div className="text-xs font-semibold uppercase tracking-wide">{label}</div>
    <div className="mt-2 text-2xl font-semibold">{value}</div>
    {hint ? <div className="mt-2 text-xs opacity-80">{hint}</div> : null}
  </div>
);

const emptyBarOption = (title: string) => ({
  animation: false,
  title: { text: title, left: "center", top: "center", textStyle: { fontSize: 13, color: "#94a3b8", fontWeight: 500 } },
  xAxis: { show: false, type: "category", data: [] },
  yAxis: { show: false, type: "value" },
  series: [],
});

const buildAccuracyChart = (data: any) => {
  const x = data?.x || [];
  if (!x.length) return emptyBarOption("No accuracy data");
  return {
    color: ["#0f766e", "#f59e0b", "#ef4444"],
    tooltip: { trigger: "axis" },
    legend: { top: 0, textStyle: { color: "#475569", fontSize: 11 } },
    grid: { left: 52, right: 18, top: 34, bottom: 30 },
    xAxis: {
      type: "category",
      data: x,
      axisLabel: { color: "#64748b", fontSize: 11 },
      axisLine: { lineStyle: { color: "#cbd5e1" } },
    },
    yAxis: {
      type: "value",
      name: "Delta (°)",
      nameTextStyle: { color: "#64748b", fontSize: 11 },
      axisLabel: { color: "#64748b", fontSize: 11 },
      splitLine: { lineStyle: { color: "#e2e8f0" } },
    },
    series: [
      {
        name: "Fleet avg Δ",
        type: "line",
        smooth: true,
        symbol: "none",
        color: "#0f766e",
        itemStyle: { color: "#0f766e" },
        lineStyle: { width: 3, color: "#0f766e" },
        data: data.avg_delta_deg || [],
      },
      {
        name: "P90 Δ",
        type: "line",
        smooth: true,
        symbol: "none",
        color: "#f59e0b",
        itemStyle: { color: "#f59e0b" },
        lineStyle: { width: 2, color: "#f59e0b" },
        data: data.p90_delta_deg || [],
      },
      {
        name: "1° threshold",
        type: "line",
        symbol: "none",
        color: "#ef4444",
        itemStyle: { color: "#ef4444" },
        lineStyle: { width: 1.5, type: "dashed", color: "#ef4444" },
        data: data.threshold_1_deg || [],
      },
    ],
  };
};

const buildCommunicationChart = (data: any) => {
  const x = data?.x || [];
  if (!x.length) return emptyBarOption("No communication data");
  return {
    color: ["#10b981", "#f59e0b", "#ef4444"],
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 0, textStyle: { color: "#475569", fontSize: 11 } },
    grid: { left: 48, right: 16, top: 34, bottom: 28 },
    xAxis: {
      type: "category",
      data: x,
      axisLabel: { color: "#64748b", fontSize: 11 },
      axisLine: { lineStyle: { color: "#cbd5e1" } },
    },
    yAxis: {
      type: "value",
      name: "Trackers",
      nameTextStyle: { color: "#64748b", fontSize: 11 },
      axisLabel: { color: "#64748b", fontSize: 11 },
      splitLine: { lineStyle: { color: "#e2e8f0" } },
    },
    series: [
      { name: "Online", type: "bar", stack: "comm", itemStyle: { color: "#10b981" }, data: data.online_trackers || [] },
      { name: "Stale", type: "bar", stack: "comm", itemStyle: { color: "#f59e0b" }, data: data.stale_trackers || [] },
      { name: "Offline", type: "bar", stack: "comm", itemStyle: { color: "#ef4444" }, data: data.offline_trackers || [] },
    ],
  };
};

const buildSimpleBarChart = (
  data: any,
  colors: string[],
  emptyTitle: string,
  options?: {
    yAxisName?: string;
    xAxisLabelFormatter?: (value: string) => string;
    valueLabelFormatter?: (value: number, index: number, values: number[]) => string;
    valueLabelPosition?: "top" | "insideTop";
  }
) => {
  const bins = data?.bins || [];
  const counts = data?.counts || [];
  if (!bins.length) return emptyBarOption(emptyTitle);
  return {
    color: colors,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    grid: { left: 54, right: 16, top: options?.valueLabelFormatter ? 28 : 12, bottom: 34 },
    xAxis: {
      type: "category",
      data: bins,
      axisLabel: {
        color: "#64748b",
        fontSize: 11,
        interval: 0,
        formatter: (value: string) => (options?.xAxisLabelFormatter ? options.xAxisLabelFormatter(value) : value),
      },
      axisLine: { lineStyle: { color: "#cbd5e1" } },
    },
    yAxis: {
      type: "value",
      name: options?.yAxisName,
      nameTextStyle: { color: "#64748b", fontSize: 11 },
      axisLabel: {
        color: "#64748b",
        fontSize: 11,
        formatter: (value: number) => Number(value).toLocaleString(),
      },
      splitLine: { lineStyle: { color: "#e2e8f0" } },
    },
    series: [
      {
        type: "bar",
        label: options?.valueLabelFormatter
          ? {
              show: true,
              position: options.valueLabelPosition || "top",
              color: "#475569",
              fontSize: 10,
              fontWeight: 600,
              formatter: (params: any) => options.valueLabelFormatter?.(Number(params.value || 0), params.dataIndex, counts) || "",
            }
          : { show: false },
        data: counts.map((value: number, index: number) => ({
          value,
          itemStyle: { color: colors[index % colors.length], borderRadius: [8, 8, 0, 0] },
        })),
      },
    ],
  };
};

const buildDeltaDistributionChart = (data: any) =>
  buildSimpleBarChart(data, ["#10b981", "#22c55e", "#f59e0b", "#fb7185", "#ef4444"], "No delta data", {
    yAxisName: "Samples",
    valueLabelFormatter: (value, _index, values) => {
      const total = values.reduce((sum, current) => sum + Number(current || 0), 0);
      if (!total || value <= 0) return "";
      return `${((value / total) * 100).toFixed(1)}%`;
    },
  });

const buildBatterySocDistributionChart = (data: any) =>
  buildSimpleBarChart(data, ["#f43f5e", "#f59e0b", "#3b82f6", "#4f46e5"], "No SoC data", {
    yAxisName: "Trackers",
    xAxisLabelFormatter: (value) => (/%$/.test(value) ? value : `${value}%`),
    valueLabelFormatter: (value) => (value > 0 ? Number(value).toLocaleString() : ""),
  });

const buildWindChart = (data: any, options?: { enableZoom?: boolean }) => {
  const x = data?.x || [];
  if (!x.length) return emptyBarOption("No wind/angle data");
  const enableZoom = options?.enableZoom === true;
  return {
    color: ["#8b5cf6", "#c084fc", "#0ea5e9"],
    tooltip: { trigger: "axis" },
    legend: { top: 0, textStyle: { color: "#475569", fontSize: 11 } },
    grid: { left: 42, right: 44, top: 34, bottom: enableZoom ? 72 : 30 },
    dataZoom: enableZoom
      ? [
          { type: "inside", xAxisIndex: 0 },
          { type: "slider", xAxisIndex: 0, height: 16, bottom: 24 },
        ]
      : [],
    xAxis: {
      type: "category",
      data: x,
      axisLabel: { color: "#64748b", fontSize: 11 },
      axisLine: { lineStyle: { color: "#cbd5e1" } },
    },
    yAxis: [
      {
        type: "value",
        name: "Angle (°)",
        nameTextStyle: { color: "#64748b", fontSize: 11 },
        axisLabel: { color: "#64748b", fontSize: 11 },
        splitLine: { lineStyle: { color: "#e2e8f0" } },
      },
      {
        type: "value",
        name: "Wind (m/s)",
        nameTextStyle: { color: "#64748b", fontSize: 11 },
        axisLabel: { color: "#64748b", fontSize: 11 },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: "Current angle",
        type: "line",
        smooth: true,
        symbol: "none",
        color: "#8b5cf6",
        itemStyle: { color: "#8b5cf6" },
        lineStyle: { width: 3, color: "#8b5cf6" },
        data: data.avg_current_angle_deg || [],
      },
      {
        name: "Target angle",
        type: "line",
        smooth: true,
        symbol: "none",
        color: "#c084fc",
        itemStyle: { color: "#c084fc" },
        lineStyle: { width: 2, color: "#c084fc" },
        data: data.avg_target_angle_deg || [],
      },
      {
        name: "Wind speed",
        type: "line",
        yAxisIndex: 1,
        smooth: true,
        symbol: "none",
        color: "#0ea5e9",
        itemStyle: { color: "#0ea5e9" },
        lineStyle: { width: 2, color: "#0ea5e9" },
        markLine: {
          symbol: "none",
          lineStyle: { color: "#ef4444", width: 1.5, type: "dashed" },
          label: { formatter: "Stow threshold", color: "#ef4444" },
          data: data.stow_threshold_mps?.[0] != null ? [{ yAxis: data.stow_threshold_mps[0] }] : [],
        },
        data: data.wind_speed_mps || [],
      },
    ],
  };
};

export default function TrackerReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.user_role === "admin";
  const [sites, setSites] = React.useState<SiteOption[]>([]);
  const [selectedSite, setSelectedSite] = React.useState<string>("");
  const [date, setDate] = React.useState<string>(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = React.useState(false);
  const [regenerating, setRegenerating] = React.useState(false);
  const [pdfGenerating, setPdfGenerating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [warning, setWarning] = React.useState<string | null>(null);
  const [report, setReport] = React.useState<any>(null);
  const [status, setStatus] = React.useState<string>("");

  React.useEffect(() => {
    const loadSites = async () => {
      try {
        const data = await listSites();
        setSites(data || []);
        if (data?.length) {
          setSelectedSite(String(data[0].id));
        }
      } catch (err) {
        setError("Failed to load sites");
      }
    };
    loadSites();
  }, []);

  React.useEffect(() => {
    if (!selectedSite || !date) return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, date]);

  const fetchReport = async () => {
    if (!selectedSite || !date) return;
    setLoading(true);
    setError(null);
    setWarning(null);
    try {
      const data = await getTrackerSiteReport(selectedSite, date);
      setReport(data.payload);
      setStatus(data.status);
      const warnings = data?.payload?.meta?.warnings;
      if (Array.isArray(warnings) && warnings.length) {
        setWarning(warnings.join(" "));
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setReport(null);
        setStatus("missing");
        setError("No tracker report found for this date.");
      } else {
        setError("Failed to load tracker report");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!selectedSite || !date) return;
    setRegenerating(true);
    setError(null);
    setWarning(null);
    try {
      const data = await regenerateTrackerReport(selectedSite, date);
      setReport(data.payload);
      setStatus(data.status);
      const warnings = data?.payload?.meta?.warnings;
      if (data?.warning) {
        setWarning(data.warning);
      } else if (Array.isArray(warnings) && warnings.length) {
        setWarning(warnings.join(" "));
      }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setWarning(err?.response?.data?.detail || "Tracker report could not be regenerated.");
      } else if (err?.response?.status === 403) {
        setError("You are not allowed to regenerate tracker reports.");
      } else {
        setError("Failed to regenerate tracker report");
      }
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!report) {
      setError("Load a tracker report before downloading PDF.");
      return;
    }
    setPdfGenerating(true);
    setError(null);
    try {
      const dateLabel = date ? new Date(date).toLocaleDateString() : new Date().toLocaleDateString();
      const reportTimezone = meta.timezone || "UTC";
      const siteLocation = [meta.city, meta.country].filter(Boolean).join(", ") || "—";
      const safeSite = String(siteName || "site").replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");

      const summaryRows = [
        ["Trackers", formatCount(meta.tracker_count)],
        ["Arrays", formatCount(meta.array_count)],
        ["Tracking compliance (% within ±1°)", formatPercent(summary.tracking_compliance_score_pct)],
        ["Average absolute delta", formatNumber(summary.avg_delta_deg, 2, "°")],
        ["Median absolute delta", formatNumber(summary.median_delta_deg, 2, "°")],
        ["Communication availability", formatPercent(summary.communication_availability_pct)],
      ];

      const thresholdRows = [
        ["Value considered stale after", formatCount(meta?.thresholds?.comm_stale_seconds) + " s"],
        ["Value considered offline after", formatCount(meta?.thresholds?.comm_offline_seconds) + " s"],
        ["Resume threshold", formatNumber(meta?.thresholds?.resume_wind_speed_mps, 1, "m/s")],
        ["Stow threshold", formatNumber(meta?.thresholds?.stow_wind_speed_mps, 1, "m/s")],
      ];

      const executiveSummaryRows = [
        ["Online communication share", ratioPercent(summary.online_tracker_minutes, totalCommSamples)],
        ["Stale communication share", ratioPercent(summary.stale_tracker_minutes, totalCommSamples)],
        ["Offline communication share", ratioPercent(summary.offline_tracker_minutes, totalCommSamples)],
        [
          "Trackers with offline periods",
          trackerCount > 0 ? `${formatCount(trackersWithOfflinePeriods)} (${ratioPercent(trackersWithOfflinePeriods, trackerCount)})` : "—",
        ],
        ["Average wind", formatNumber(summary.avg_wind_speed_mps, 1, "m/s")],
        ["Maximum wind", formatNumber(summary.max_wind_speed_mps, 1, "m/s")],
        ["Minutes above resume threshold", `${formatCount(summary.minutes_above_resume_threshold)} min`],
        ["Minutes above stow threshold", `${formatCount(summary.minutes_above_stow_threshold)} min`],
      ];

      const accuracyKpiRows = [
        ["% within ±0.5°", formatPercent(summary.within_0_5_deg_pct)],
        ["% within ±1°", formatPercent(summary.within_1_deg_pct)],
        ["% within ±2°", formatPercent(summary.within_2_deg_pct)],
      ];

      const summaryNotes = [
        "Tracking compliance = share of valid online tracker samples within ±1° of target angle.",
        "Average and median absolute delta = absolute tracker-angle error versus target angle, using valid online samples only.",
        "Trackers with offline periods = trackers that crossed the offline threshold at any point during the report day; they are not necessarily offline at the end of the day.",
        "Value considered stale after / offline after = time since the last successful tracker communication.",
        "Communication availability and communication shares are calculated from tracker communication samples across the full report day.",
      ];

      const addFooter = (doc: jsPDF) => {
        const total = doc.getNumberOfPages();
        for (let i = 1; i <= total; i += 1) {
          doc.setPage(i);
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          const footerY = pageHeight - 12;
          doc.setFontSize(9);
          doc.setTextColor(33, 37, 41);
          doc.text("created with PV-report.com", PAGE_MARGIN_PT, footerY);
          doc.text(`page ${i}/${total}`, pageWidth / 2, footerY, { align: "center" });
          doc.text("powered by T-NRG", pageWidth - PAGE_MARGIN_PT, footerY, { align: "right" });
        }
      };

      const headerBar = (doc: jsPDF, title: string, includeDate = false) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const y = PAGE_MARGIN_PT;
        doc.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
        doc.rect(PAGE_MARGIN_PT, y, pageWidth - PAGE_MARGIN_PT * 2, 24, "F");
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text(title, PAGE_MARGIN_PT + 6, y + 16);
        if (includeDate) {
          doc.text(`Report date: ${dateLabel}`, pageWidth - PAGE_MARGIN_PT - 6, y + 16, { align: "right" });
        }
        return y + 32;
      };

      const addSmallNotesBlock = (doc: jsPDF, title: string, lines: string[], x: number, y: number, width: number) => {
        let cursorY = y;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...SLATE);
        doc.text(title, x, cursorY);
        cursorY += 10;
        doc.setFont("helvetica", "normal");
        for (const line of lines) {
          const wrapped = doc.splitTextToSize(line, width);
          doc.text(wrapped, x, cursorY);
          cursorY += wrapped.length * 8 + 4;
        }
        return cursorY;
      };

      const addChartImage = (
        doc: jsPDF,
        title: string,
        imageData: string | null,
        yStart: number,
        imageHeight: number,
        options?: { landscape?: boolean }
      ) => {
        if (!imageData) return yStart;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let y = yStart;
        const sectionHeight = 24 + imageHeight + 10;
        if (y + sectionHeight + PAGE_MARGIN_PT > pageHeight) {
          doc.addPage("a4", options?.landscape ? "landscape" : "portrait");
          y = headerBar(doc, "TRACKER DAILY REPORT (cont.)", true);
        }
        doc.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
        doc.rect(PAGE_MARGIN_PT, y, pageWidth - PAGE_MARGIN_PT * 2, 18, "F");
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(title, PAGE_MARGIN_PT + 6, y + 13);
        y += 28;
        const imgWidth = pageWidth - PAGE_MARGIN_PT * 2;
        doc.addImage(imageData, "PNG", PAGE_MARGIN_PT, y, imgWidth, imageHeight);
        return y + imageHeight + 12;
      };

      const accuracyChart = await chartToDataUrl(buildAccuracyChart(charts.accuracy_over_time), 1200, 420);
      const deltaChart = await chartToDataUrl(buildDeltaDistributionChart(charts.delta_distribution), 820, 320);
      const socChart = await chartToDataUrl(buildBatterySocDistributionChart(charts.battery_soc_distribution), 820, 320);
      const communicationChart = await chartToDataUrl(buildCommunicationChart(charts.communication_over_time), 1200, 360);
      const windChart = await chartToDataUrl(buildWindChart(charts.wind_vs_fleet_angle), 1200, 420);

      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      let pageWidth = doc.internal.pageSize.getWidth();
      let pageHeight = doc.internal.pageSize.getHeight();

      // Cover
      doc.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
      doc.rect(PAGE_MARGIN_PT, PAGE_MARGIN_PT, 10, pageHeight - PAGE_MARGIN_PT * 2, "F");
      doc.setFontSize(12);
      doc.setTextColor(33, 37, 41);
      doc.text(dateLabel, pageWidth - PAGE_MARGIN_PT, PAGE_MARGIN_PT + 12, { align: "right" });
      doc.setFontSize(30);
      doc.setFont("helvetica", "bold");
      doc.text("TRACKER DAILY REPORT", pageWidth / 2, pageHeight / 2 - 14, { align: "center" });
      doc.setFontSize(20);
      doc.text(siteName, pageWidth / 2, pageHeight / 2 + 16, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(`Date: ${dateLabel} • Timezone: ${reportTimezone}`, pageWidth / 2, pageHeight / 2 + 40, { align: "center" });
      doc.text(siteLocation, pageWidth / 2, pageHeight / 2 + 60, { align: "center" });

      // Summary page
      doc.addPage();
      let y = headerBar(doc, "TRACKER DAILY REPORT SUMMARY", true);

      autoTable(doc, {
        startY: y,
        head: [["Field", "Value"]],
        body: [
          ["Site", siteName],
          ["Location", siteLocation],
          ["Status", status || "stored"],
          ["Timezone", reportTimezone],
        ],
        theme: "grid",
        styles: { fontSize: 9 },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255] },
        margin: { left: PAGE_MARGIN_PT, right: PAGE_MARGIN_PT },
        tableWidth: pageWidth - PAGE_MARGIN_PT * 2,
      });
      y = (((doc as any).lastAutoTable?.finalY as number | undefined) || y) + 16;

      autoTable(doc, {
        startY: y,
        head: [["Metric", "Value"]],
        body: summaryRows,
        theme: "grid",
        styles: { fontSize: 9 },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255] },
        margin: { left: PAGE_MARGIN_PT, right: PAGE_MARGIN_PT },
        tableWidth: (pageWidth - PAGE_MARGIN_PT * 2 - 12) / 2,
      });
      const leftTableY = ((doc as any).lastAutoTable?.finalY as number | undefined) || y;

      autoTable(doc, {
        startY: y,
        head: [["Threshold", "Value"]],
        body: thresholdRows,
        theme: "grid",
        styles: { fontSize: 9 },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255] },
        margin: { left: PAGE_MARGIN_PT + (pageWidth - PAGE_MARGIN_PT * 2 - 12) / 2 + 12, right: PAGE_MARGIN_PT },
        tableWidth: (pageWidth - PAGE_MARGIN_PT * 2 - 12) / 2,
      });
      y = Math.max(leftTableY, ((doc as any).lastAutoTable?.finalY as number | undefined) || y) + 16;

      autoTable(doc, {
        startY: y,
        head: [["Executive summary", "Value"]],
        body: executiveSummaryRows,
        theme: "grid",
        styles: { fontSize: 9 },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255] },
        margin: { left: PAGE_MARGIN_PT, right: PAGE_MARGIN_PT },
        tableWidth: pageWidth - PAGE_MARGIN_PT * 2,
      });
      y = (((doc as any).lastAutoTable?.finalY as number | undefined) || y) + 14;

      autoTable(doc, {
        startY: y,
        head: [["Accuracy KPI", "Value"]],
        body: accuracyKpiRows,
        theme: "grid",
        styles: { fontSize: 9 },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255] },
        margin: { left: PAGE_MARGIN_PT, right: PAGE_MARGIN_PT },
        tableWidth: pageWidth - PAGE_MARGIN_PT * 2,
      });
      y = (((doc as any).lastAutoTable?.finalY as number | undefined) || y) + 12;
      addSmallNotesBlock(doc, "Notes", summaryNotes, PAGE_MARGIN_PT, y, pageWidth - PAGE_MARGIN_PT * 2);

      // Charts page 1
      doc.addPage("a4", "landscape");
      pageWidth = doc.internal.pageSize.getWidth();
      pageHeight = doc.internal.pageSize.getHeight();
      y = headerBar(doc, "TRACKING ACCURACY", true);
      y = addChartImage(doc, "Fleet delta over time", accuracyChart, y, 220, { landscape: true });

      const deltaY = y;
      const halfWidth = (pageWidth - PAGE_MARGIN_PT * 2 - 12) / 2;
      const chartHeight = 140;
      const chartTop = deltaY + 24;
      const chartNoteY = chartTop + chartHeight + 12;

      if (deltaChart) {
        doc.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
        doc.rect(PAGE_MARGIN_PT, deltaY, halfWidth, 18, "F");
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text("DELTA DISTRIBUTION", PAGE_MARGIN_PT + 6, deltaY + 13);
        doc.addImage(deltaChart, "PNG", PAGE_MARGIN_PT, chartTop, halfWidth, chartHeight);
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        doc.text(
          "Bars show valid online tracker samples grouped by absolute tracking error.",
          PAGE_MARGIN_PT,
          chartNoteY
        );
      }

      if (socChart) {
        const rightX = PAGE_MARGIN_PT + halfWidth + 12;
        doc.setFillColor(PDF_BLUE[0], PDF_BLUE[1], PDF_BLUE[2]);
        doc.rect(rightX, deltaY, halfWidth, 18, "F");
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text("BATTERY SOC DISTRIBUTION", rightX + 6, deltaY + 13);
        doc.addImage(socChart, "PNG", rightX, chartTop, halfWidth, chartHeight);
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        doc.text("Bars show how many trackers fall into each battery SoC range.", rightX, chartNoteY);
      }

      const footnoteY = chartNoteY + 20;
      const noteWidth = (pageWidth - PAGE_MARGIN_PT * 2 - 12) / 2;
      addSmallNotesBlock(
        doc,
        "How to read Fleet avg Δ",
        [
          "Fleet average delta is the average absolute difference between current tracker angle and target angle across valid online trackers.",
          "Lower is better. Sustained periods above 1° suggest fleet-wide misalignment.",
        ],
        PAGE_MARGIN_PT,
        footnoteY,
        noteWidth
      );
      addSmallNotesBlock(
        doc,
        "How to read P90 Δ",
        [
          "P90 delta shows the performance of the slower tail of the fleet.",
          "If P90 rises much faster than the fleet average, a subset of trackers is lagging or not following the controller command.",
        ],
        PAGE_MARGIN_PT + noteWidth + 12,
        footnoteY,
        noteWidth
      );

      // Charts page 2
      doc.addPage("a4", "landscape");
      pageWidth = doc.internal.pageSize.getWidth();
      pageHeight = doc.internal.pageSize.getHeight();
      y = headerBar(doc, "COMMUNICATION AND WIND", true);
      y = addChartImage(doc, "Communication state over time", communicationChart, y, 220, { landscape: true });
      y = addChartImage(doc, "Wind vs fleet angle", windChart, y, 250, { landscape: true });

      // Exceptions page
      doc.addPage();
      pageWidth = doc.internal.pageSize.getWidth();
      pageHeight = doc.internal.pageSize.getHeight();
      y = headerBar(doc, "TRACKER EXCEPTIONS", true);
      autoTable(doc, {
        startY: y,
        head: [["Tracker", "Array", "Avg Δ", "Max Δ", "% within ±1°", "Comm avail", "Offline min", "Flags"]],
        body: (exceptions.worst_trackers || []).map((row: any) => [
          row.display_name || row.tracker_key || "—",
          row.array_name || row.transformer_name || "—",
          formatNumber(row.avg_delta_deg, 2, "°"),
          formatNumber(row.max_delta_deg, 2, "°"),
          formatPercent(row.within_1_deg_pct),
          formatPercent(row.communication_availability_pct),
          formatCount(row.offline_minutes),
          (row.notes || []).length ? row.notes.map((note: string) => note.replace(/_/g, " ")).join(", ") : "—",
        ]),
        theme: "grid",
        styles: { fontSize: 8.5, cellWidth: "wrap" },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 70 },
          7: { cellWidth: 150 },
        },
        headStyles: { fillColor: PDF_BLUE, textColor: [255, 255, 255] },
        margin: { left: PAGE_MARGIN_PT, right: PAGE_MARGIN_PT },
      });

      addFooter(doc);
      doc.save(`tracker_report_${safeSite}_${date}.pdf`);
    } catch (err) {
      console.error("Tracker PDF generation failed", err);
      setError("Failed to generate tracker report PDF.");
    } finally {
      setPdfGenerating(false);
    }
  };

  const meta = report?.meta || {};
  const summary = report?.summary || {};
  const charts = report?.charts || {};
  const windStow = report?.wind_stow || {};
  const exceptions = report?.exceptions || {};
  const siteName = meta.site_name || sites.find((site) => String(site.id) === selectedSite)?.name || "Site";
  const trackerCount = Number(meta.tracker_count || 0);
  const trackersWithOfflinePeriods = Number(summary.trackers_with_offline_periods || 0);
  const totalCommSamples =
    Number(summary.online_tracker_minutes || 0) +
    Number(summary.stale_tracker_minutes || 0) +
    Number(summary.offline_tracker_minutes || 0);

  const complianceTone = statusTone(summary.tracking_compliance_score_pct);
  const availabilityTone = statusTone(summary.communication_availability_pct);
  const worstDeltaTone =
    summary?.worst_tracker?.max_delta_deg != null && summary.worst_tracker.max_delta_deg > 2
      ? "rose"
      : summary?.worst_tracker?.max_delta_deg != null && summary.worst_tracker.max_delta_deg > 1
        ? "amber"
        : "emerald";

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Tracker Reports</div>
            <div className="text-4xl font-semibold tracking-tight text-slate-900">{siteName}</div>
            <div className="text-sm text-slate-500">
              Report date: <span className="font-medium text-slate-700">{formatDateLabel(date)}</span>
              {meta?.timezone ? (
                <>
                  {" "}
                  | Timezone: <span className="font-medium text-slate-700">{meta.timezone}</span>
                </>
              ) : null}
            </div>
            <div className="text-xs text-slate-500">
              {status === "partial" ? "Partial report for today" : status === "fallback" ? "Fallback report" : "Stored tracker daily report"}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-[minmax(180px,1fr)_160px_auto_auto_auto]">
            <select
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm shadow-sm"
              value={selectedSite}
              onChange={(event) => setSelectedSite(event.target.value)}
            >
              {sites.map((site) => (
                <option key={site.id} value={String(site.id)}>
                  {site.name}
                </option>
              ))}
            </select>
            <input
              type="date"
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm shadow-sm"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            <button
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={fetchReport}
              disabled={loading || !selectedSite}
            >
              {loading ? "Loading..." : "Load report"}
            </button>
            <button
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleDownloadPdf}
              disabled={pdfGenerating || !report}
            >
              {pdfGenerating ? "Building PDF..." : "Download PDF"}
            </button>
            {isAdmin ? (
              <button
                className="rounded bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-500 disabled:opacity-50"
                onClick={handleRegenerate}
                disabled={regenerating || !selectedSite}
              >
                {regenerating ? "Regenerating..." : "Regenerate"}
              </button>
            ) : null}
          </div>
        </div>

        {warning ? (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{warning}</div>
        ) : null}
        {error ? <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div> : null}
      </section>

      {report ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <SummaryKpi
              label="Tracking Compliance"
              value={formatPercent(summary.tracking_compliance_score_pct)}
              hint="Share of valid online samples within ±1°"
              tone={complianceTone as keyof typeof toneClasses}
            />
            <SummaryKpi
              label="Avg Absolute Delta"
              value={formatNumber(summary.avg_delta_deg, 2, "°")}
              hint={`Median ${formatNumber(summary.median_delta_deg, 2, "°")}`}
              tone={summary.avg_delta_deg != null && summary.avg_delta_deg > 1 ? "amber" : "emerald"}
            />
            <SummaryKpi
              label="Communication Availability"
              value={formatPercent(summary.communication_availability_pct)}
              hint={
                trackerCount > 0
                  ? `${ratioPercent(trackersWithOfflinePeriods, trackerCount)} of trackers had at least one offline period`
                  : "No tracker communication summary available"
              }
              tone={availabilityTone as keyof typeof toneClasses}
            />
            <SummaryKpi
              label="Worst Tracker"
              value={formatNumber(summary?.worst_tracker?.max_delta_deg, 2, "°")}
              hint={summary?.worst_tracker?.tracker_key ? `${summary.worst_tracker.tracker_key}` : "No tracker outlier"}
              tone={worstDeltaTone as keyof typeof toneClasses}
            />
            <SummaryKpi
              label="Maximum Wind"
              value={formatNumber(summary.max_wind_speed_mps, 1, "m/s")}
              hint={`Daily peak wind speed | ${formatCount(summary.minutes_above_stow_threshold)} min above stow threshold`}
              tone="blue"
            />
          </section>

          <ToggleCard title="Executive Summary" defaultOpen>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Trackers</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{formatCount(meta.tracker_count)}</div>
                <div className="mt-2 text-xs text-slate-500">{formatCount(meta.array_count)} arrays represented</div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Communication</div>
                <div className="mt-2 text-sm text-slate-700">
                  Online communication share: <span className="font-semibold text-slate-900">{ratioPercent(summary.online_tracker_minutes, totalCommSamples)}</span>
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  Stale communication share: <span className="font-semibold text-slate-900">{ratioPercent(summary.stale_tracker_minutes, totalCommSamples)}</span>
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  Offline communication share: <span className="font-semibold text-slate-900">{ratioPercent(summary.offline_tracker_minutes, totalCommSamples)}</span>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Share of tracker communication samples across the full report day.
                </div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wind & Stow</div>
                <div className="mt-2 text-sm text-slate-700">
                  Avg wind: <span className="font-semibold text-slate-900">{formatNumber(summary.avg_wind_speed_mps, 1, "m/s")}</span>
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  Above resume: <span className="font-semibold text-slate-900">{formatCount(summary.minutes_above_resume_threshold)} min</span>
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  Above stow: <span className="font-semibold text-slate-900">{formatCount(summary.minutes_above_stow_threshold)} min</span>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Thresholds</div>
                <div className="mt-2 text-sm text-slate-700">
                  Value stale after: <span className="font-semibold text-slate-900">{formatCount(meta?.thresholds?.comm_stale_seconds)} s</span>
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  Value offline after: <span className="font-semibold text-slate-900">{formatCount(meta?.thresholds?.comm_offline_seconds)} s</span>
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  Stow threshold: <span className="font-semibold text-slate-900">{formatNumber(meta?.thresholds?.stow_wind_speed_mps, 1, "m/s")}</span>
                </div>
              </div>
            </div>
          </ToggleCard>

          <ToggleCard title="Tracking Accuracy" defaultOpen>
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="mb-3 text-sm font-semibold text-slate-900">Fleet delta over time</div>
                <ReactECharts option={buildAccuracyChart(charts.accuracy_over_time)} style={{ height: 320 }} />
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-sm text-slate-700">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">How to read Fleet avg Δ</div>
                    <div className="mt-2">
                      Fleet average delta is the average absolute difference between current tracker angle and target angle across valid online trackers.
                      Lower is better. Sustained periods above <span className="font-semibold text-slate-900">1°</span> suggest fleet-wide misalignment.
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-sm text-slate-700">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">How to read P90 Δ</div>
                    <div className="mt-2">
                      P90 delta shows the performance of the slower tail of the fleet. If P90 rises much faster than the fleet average, a subset of trackers is lagging or not following the controller command.
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="mb-3 text-sm font-semibold text-slate-900">Delta distribution</div>
                    <ReactECharts option={buildDeltaDistributionChart(charts.delta_distribution)} style={{ height: 240 }} />
                    <div className="mt-3 rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-sm text-slate-700">
                      Bars show how many valid online tracker samples fall into each absolute tracking-error bucket.
                      A strong concentration in the lower buckets indicates the fleet is following target angle consistently.
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">% within ±0.5°</div>
                      <div className="mt-2 text-2xl font-semibold text-slate-900">{formatPercent(summary.within_0_5_deg_pct)}</div>
                    </div>
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">% within ±1°</div>
                      <div className="mt-2 text-2xl font-semibold text-slate-900">{formatPercent(summary.within_1_deg_pct)}</div>
                    </div>
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">% within ±2°</div>
                      <div className="mt-2 text-2xl font-semibold text-slate-900">{formatPercent(summary.within_2_deg_pct)}</div>
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="mb-3 text-sm font-semibold text-slate-900">Battery SoC distribution</div>
                  <ReactECharts option={buildBatterySocDistributionChart(charts.battery_soc_distribution)} style={{ height: 240 }} />
                  <div className="mt-3 text-sm text-slate-600">Bars show how many trackers fall into each battery SoC range (%).</div>
                </div>
              </div>
            </div>
          </ToggleCard>

          <ToggleCard title="Communication" defaultOpen>
            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="mb-3 text-sm font-semibold text-slate-900">Communication state over time</div>
              <ReactECharts option={buildCommunicationChart(charts.communication_over_time)} style={{ height: 280 }} />
            </div>
          </ToggleCard>

          <ToggleCard title="Wind & Stow" defaultOpen>
            <div className="grid gap-4 xl:grid-cols-[1.45fr_0.85fr]">
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="mb-3 text-sm font-semibold text-slate-900">Wind vs fleet angle</div>
                <ReactECharts option={buildWindChart(charts.wind_vs_fleet_angle, { enableZoom: true })} style={{ height: 380 }} />
              </div>
              <div className="grid gap-4">
                <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Daily wind summary</div>
                  <div className="mt-2 text-sm text-slate-700">
                    Avg wind: <span className="font-semibold text-slate-900">{formatNumber(summary.avg_wind_speed_mps, 1, "m/s")}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-700">
                    Max wind: <span className="font-semibold text-slate-900">{formatNumber(summary.max_wind_speed_mps, 1, "m/s")}</span>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">High-wind behavior</div>
                  <div className="mt-2 text-sm text-slate-700">
                    Avg fleet angle: <span className="font-semibold text-slate-900">{formatNumber(windStow.avg_fleet_angle_during_high_wind_deg, 1, "°")}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-700">
                    Avg target angle: <span className="font-semibold text-slate-900">{formatNumber(windStow.avg_target_angle_during_high_wind_deg, 1, "°")}</span>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Threshold time</div>
                  <div className="mt-2 text-sm text-slate-700">
                    Above resume: <span className="font-semibold text-slate-900">{formatCount(windStow.minutes_above_resume_threshold)} min</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-700">
                    Above stow: <span className="font-semibold text-slate-900">{formatCount(windStow.minutes_above_stow_threshold)} min</span>
                  </div>
                </div>
              </div>
            </div>
          </ToggleCard>

          <ToggleCard title="Exceptions" defaultOpen>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {["Tracker", "Array", "Avg Δ", "Max Δ", "% within ±1°", "Comm avail", "Offline min", "Flags"].map((header) => (
                      <th key={header} className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {(exceptions.worst_trackers || []).map((row: any) => (
                    <tr key={row.tracker_key} className="hover:bg-slate-50">
                      <td className="px-3 py-3 font-semibold text-slate-900">{row.display_name || row.tracker_key}</td>
                      <td className="px-3 py-3 text-slate-600">{row.array_name || row.transformer_name || "—"}</td>
                      <td className="px-3 py-3 text-slate-700">{formatNumber(row.avg_delta_deg, 2, "°")}</td>
                      <td className="px-3 py-3 text-slate-700">{formatNumber(row.max_delta_deg, 2, "°")}</td>
                      <td className="px-3 py-3 text-slate-700">{formatPercent(row.within_1_deg_pct)}</td>
                      <td className="px-3 py-3 text-slate-700">{formatPercent(row.communication_availability_pct)}</td>
                      <td className="px-3 py-3 text-slate-700">{formatCount(row.offline_minutes)}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-2">
                          {(row.notes || []).length ? (
                            row.notes.map((note: string) => (
                              <span key={note} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700">
                                {note.replace(/_/g, " ")}
                              </span>
                            ))
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!(exceptions.worst_trackers || []).length ? (
                <div className="py-8 text-center text-sm text-slate-500">No exception rows available for this day.</div>
              ) : null}
            </div>
          </ToggleCard>
        </>
      ) : null}
    </div>
  );
}
